import React, { useState, useRef } from 'react';
import { GalleryVerticalEnd, Download, Loader2, ArrowLeft, RotateCw, Trash2, Copy, GripVertical, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAllPageThumbnails, saveReorderedPdf } from '../utils/pdfPageManipulation.js';

const RearrangePdf = () => {
    const [originalFile, setOriginalFile] = useState(null);
    const [pages, setPages] = useState([]); // Array of { id, originalIndex, thumbnail, rotation }
    const [isThinking, setIsThinking] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);

    const fileInputRef = useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setOriginalFile(file);
            setIsThinking(true);
            try {
                const thumbnails = await getAllPageThumbnails(file);
                setPages(thumbnails);
            } catch (error) {
                console.error("Error generating thumbnails", error);
                alert("Failed to load PDF pages. Please try another file.");
                setOriginalFile(null);
            } finally {
                setIsThinking(false);
            }
        }
    };

    const handleDropUpload = async (e) => {
        e.preventDefault();
        const file = Array.from(e.dataTransfer.files).find(f => f.type === 'application/pdf');
        if (file) {
            setOriginalFile(file);
            setIsThinking(true);
            try {
                const thumbnails = await getAllPageThumbnails(file);
                setPages(thumbnails);
            } catch (error) {
                console.error("Error generating thumbnails", error);
                alert("Failed to load PDF pages.");
            } finally {
                setIsThinking(false);
            }
        }
    };

    const handleDragOverUpload = (e) => {
        e.preventDefault();
    };

    // Page Manipulation
    const rotatePage = (index) => {
        setPages(prev => prev.map((p, i) => {
            if (i === index) {
                return { ...p, rotation: (p.rotation + 90) % 360 };
            }
            return p;
        }));
    };

    const deletePage = (index) => {
        setPages(prev => prev.filter((_, i) => i !== index));
    };

    const duplicatePage = (index) => {
        setPages(prev => {
            const newPages = [...prev];
            const pageToCopy = { ...newPages[index], id: crypto.randomUUID() };
            newPages.splice(index + 1, 0, pageToCopy);
            return newPages;
        });
    };

    // Reordering DnD
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        // Transparent drag image or generic
    };

    const handleDragEnter = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        setPages(prev => {
            const newPages = [...prev];
            const draggedItem = newPages[draggedIndex];
            newPages.splice(draggedIndex, 1);
            newPages.splice(index, 0, draggedItem);
            return newPages;
        });
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSave = async () => {
        if (!originalFile || pages.length === 0) return;

        setIsThinking(true);
        try {
            const newPdfBlob = await saveReorderedPdf(originalFile, pages);
            const url = URL.createObjectURL(newPdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `rearranged_${originalFile.name}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error saving PDF", error);
            alert("Failed to save PDF.");
        } finally {
            setIsThinking(false);
        }
    };

    return (
        <div className="container animate-fade-in">
            <div style={{ marginBottom: '2rem' }}>
                <Link to="/tools/pdf" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
                    <ArrowLeft size={16} /> Back to PDF Tools
                </Link>
            </div>

            <header className="flex-center" style={{ flexDirection: 'column', marginBottom: '3rem' }}>
                <div className="flex-center" style={{
                    width: '64px',
                    height: '64px',
                    background: 'linear-gradient(135deg, var(--primary), var(--primary-hover))',
                    borderRadius: '1rem',
                    marginBottom: '1rem',
                    color: 'white',
                    boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4)'
                }}>
                    <GalleryVerticalEnd size={32} />
                </div>
                <h1>Rearrange Pages</h1>
                <p>Drag and drop to reorder pages, remove duplicates, or rotate</p>
            </header>

            <main style={{ maxWidth: '1000px', margin: '0 auto' }}>
                {!originalFile || pages.length === 0 ? (
                    <div
                        className="glass-card"
                        style={{ marginBottom: '2rem', textAlign: 'center', borderStyle: 'dashed', borderWidth: '2px' }}
                        onDrop={handleDropUpload}
                        onDragOver={handleDragOverUpload}
                    >
                        <input
                            type="file"
                            id="file-upload"
                            accept=".pdf"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                        />
                        <label htmlFor="file-upload" className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                            {isThinking ? <Loader2 className="animate-spin" /> : "Select PDF File"}
                        </label>
                        <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                            {isThinking ? "Processing pages..." : "or drag and drop text file here"}
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3 style={{ marginBottom: '0.25rem' }}>{originalFile.name}</h3>
                                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                                    {pages.length} pages • {(originalFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setOriginalFile(null);
                                        setPages([]);
                                    }}
                                >
                                    Start Over
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={isThinking}
                                >
                                    {isThinking ? <Loader2 className="animate-spin" /> : <><Download size={18} /> Download PDF</>}
                                </button>
                            </div>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '1.5rem'
                        }}>
                            {pages.map((page, index) => (
                                <div
                                    key={page.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    // onDragEnter={(e) => handleDragEnter(e, index)} // Trying simpler approach for grid
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        handleDragEnter(e, index);
                                    }}
                                    onDragEnd={handleDragEnd}
                                    className="glass-card"
                                    style={{
                                        padding: '1rem',
                                        cursor: 'move',
                                        transition: 'all 0.2s ease',
                                        opacity: draggedIndex === index ? 0.4 : 1,
                                        transform: draggedIndex === index ? 'scale(0.95)' : 'none',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <div style={{
                                        position: 'relative',
                                        aspectRatio: '3/4',
                                        background: '#f1f5f9',
                                        borderRadius: '0.5rem',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <img
                                            src={page.thumbnail}
                                            alt={`Page ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                transform: `rotate(${page.rotation}deg)`,
                                                transition: 'transform 0.3s ease'
                                            }}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: '0.5rem',
                                            left: '0.5rem',
                                            background: 'rgba(0,0,0,0.6)',
                                            color: 'white',
                                            padding: '0.2rem 0.5rem',
                                            borderRadius: '0.25rem',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {index + 1}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: 'auto' }}>
                                        <button
                                            className="btn-icon"
                                            title="Rotate"
                                            onClick={() => rotatePage(index)}
                                            style={{
                                                background: 'var(--bg-gradient-start)', border: '1px solid var(--border)',
                                                padding: '0.5rem', borderRadius: '0.25rem', cursor: 'pointer'
                                            }}
                                        >
                                            <RotateCw size={16} />
                                        </button>
                                        <button
                                            className="btn-icon"
                                            title="Duplicate"
                                            onClick={() => duplicatePage(index)}
                                            style={{
                                                background: 'var(--bg-gradient-start)', border: '1px solid var(--border)',
                                                padding: '0.5rem', borderRadius: '0.25rem', cursor: 'pointer'
                                            }}
                                        >
                                            <Copy size={16} />
                                        </button>
                                        <button
                                            className="btn-icon"
                                            title="Delete"
                                            onClick={() => deletePage(index)}
                                            style={{
                                                background: '#fee2e2', border: '1px solid #fecaca', color: '#dc2626',
                                                padding: '0.5rem', borderRadius: '0.25rem', cursor: 'pointer'
                                            }}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {/* Add Page Button - maybe nice to have later, but duplication works for now */}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default RearrangePdf;
